import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function Kontakt() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot
  });
  const [formStatus, setFormStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitting: true, success: false, error: null });

    if (!formData.name || !formData.phone || !formData.email || !formData.description) {
      setFormStatus({
        submitting: false,
        success: false,
        error: 'Venligst udfyld alle påkrævede felter.',
      });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormStatus({ submitting: false, success: true, error: null });
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        });
      } else {
        const errData = await response.json();
        setFormStatus({
          submitting: false,
          success: false,
          error: errData.message || 'Der skete en fejl ved afsendelse. Prøv venligst igen.',
        });
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setFormStatus({
        submitting: false,
        success: false,
        error: 'Netværksfejl. Kontroller din internetforbindelse og prøv igen.',
      });
    }
  };

  return (
    <div className="bg-brand-navy-50 min-h-screen">
      <SEO
        title="Kontakt Entreprenør Jan Nørgaard | Få et tilbud"
        description="Kontakt Entreprenør Jan Nørgaard ApS. Ring på 20 48 38 18 eller send en besked for et uforpligtende tilbud på kloak- eller entreprenørarbejde."
        image="https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/af349c16-3f04-4dd3-a862-642e24736c63/udgravning-minigraver-jordarbejde.jpg"
      />
      {/* Hero Section */}
      <section className="bg-brand-navy-900 text-white py-10 md:py-14 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-15">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/af349c16-3f04-4dd3-a862-642e24736c63/udgravning-minigraver-jordarbejde.jpg"
            alt="Minigraver i færd med præcis udgravning i terræn til olietank og jordarbejde"
            title="Præcisionsudgravning og entreprenørarbejde"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="mt-3 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-5xl">
            Kontakt os i dag
          </h1>
          <p className="mt-4 max-w-2xl mx-auto text-lg text-brand-navy-200">
            Hos{' '}
            <Link
              to="/"
              className="font-bold text-white underline decoration-brand-orange-400 decoration-2 underline-offset-4 hover:text-brand-orange-400 transition-colors"
              title="Entreprenør Jan Nørgaard - Til forsiden"
            >
              Entreprenør Jan Nørgaard ApS
            </Link>{' '}
            får du professionel rådgivning og et uforpligtende tilbud på dit næste projekt.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
          {/* Left Column: Contact Details */}
          <div className="space-y-8">
            <div>
              <h2 className="mt-3 font-display text-3xl font-bold uppercase text-brand-navy-900">
                Lad os tale om dit projekt
              </h2>
              <p className="mt-4 text-brand-navy-600 leading-relaxed">
                Uanset om du har brug for en akut kloakspuling, eller du ønsker et tilbud på et større anlægs- eller støbeprojekt, er du altid velkommen til at kontakte os. Vi besvarer alle henvendelser hurtigst muligt.
              </p>
            </div>

            {/* Contact Cards */}
            <div className="bg-white rounded-lg shadow-md border border-brand-navy-100 divide-y divide-brand-navy-100">
              <div className="p-6 flex items-start gap-4">
                <div className="text-brand-orange-500 text-xl pt-1 shrink-0 w-6 text-center">
                  <i className="fa-solid fa-phone" />
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-brand-navy-500">
                    Ring direkte
                  </span>
                  <a
                    href="tel:+4520483818"
                    className="text-lg font-bold text-brand-navy-900 hover:text-brand-orange-600 transition-colors whitespace-nowrap"
                  >
                    20 48 38 18
                  </a>
                </div>
              </div>

              <div className="p-6 flex items-start gap-4">
                <div className="text-brand-orange-500 text-xl pt-1 shrink-0 w-6 text-center">
                  <i className="fa-solid fa-envelope" />
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-brand-navy-500">
                    Send en e-mail
                  </span>
                  <a
                    href="mailto:jan@jannoergaard.dk"
                    className="text-lg font-bold text-brand-navy-900 hover:text-brand-orange-600 transition-colors whitespace-nowrap"
                  >
                    jan@jannoergaard.dk
                  </a>
                </div>
              </div>

              <div className="p-6 flex items-start gap-4">
                <div className="text-brand-orange-500 text-xl pt-1 shrink-0 w-6 text-center">
                  <i className="fa-solid fa-location-dot" />
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-wider text-brand-navy-500">
                    Kontoradresse
                  </span>
                  <span className="block text-base font-bold text-brand-navy-900">
                    Entreprenør Jan Nørgaard ApS
                  </span>
                  <span className="block text-base font-medium text-brand-navy-900">
                    Ørstedvej 22, 6560 Sommersted
                  </span>
                </div>
              </div>
            </div>

          </div>

          {/* Right Column: Form */}
          <div className="bg-white rounded-lg p-8 shadow-lg border border-brand-navy-100">
            <h3 className="font-display text-xl font-bold uppercase text-brand-navy-900 mb-6">
              Udfyld formularen
            </h3>

            <form onSubmit={handleFormSubmit} className="space-y-4">
              {/* Honeypot */}
              <div className="sr-only absolute" aria-hidden="true">
                <label htmlFor="website_url">Hjemmeside URL</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                  Navn <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                  placeholder="Dit fulde navn"
                />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                    Telefonnummer <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                    placeholder="Telefonnummer"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                    E-mail <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                    placeholder="E-mail adresse"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider text-brand-navy-700 mb-1">
                  Beskrivelse af opgaven <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="description"
                  name="description"
                  required
                  rows="5"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="w-full rounded border border-brand-navy-200 px-4 py-2.5 text-sm focus:border-brand-orange-500 focus:outline-none"
                  placeholder="Beskriv dit projekt eller problem så detaljeret som muligt..."
                />
              </div>

              {formStatus.error && (
                <div className="rounded bg-red-50 p-3 text-sm font-semibold text-red-600 border border-red-200">
                  <i className="fa-solid fa-circle-exclamation mr-1.5" /> {formStatus.error}
                </div>
              )}

              {formStatus.success && (
                <div className="rounded bg-green-50 p-3 text-sm font-semibold text-green-600 border border-green-200">
                  <i className="fa-solid fa-circle-check mr-1.5" /> Tak for din henvendelse! Vi vender tilbage til dig hurtigst muligt.
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus.submitting}
                className="w-full rounded bg-brand-orange-500 py-3 font-display text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-brand-orange-600 disabled:opacity-50"
              >
                {formStatus.submitting ? (
                  <span>Sender... <i className="fa-solid fa-spinner animate-spin ml-1" /></span>
                ) : (
                  <span>Send besked <i className="fa-regular fa-paper-plane ml-1" /></span>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
